import { useCallback, useState } from 'react';
import type { AircraftTypeId, CabinConfiguration } from '../../../domain/models';
import type { SavedCabinPreset } from './cabinEditorTypes';

/**
 * Player-saved cabin presets, persisted in localStorage. Storage is a
 * per-browser convenience only: reads are fully validated and every storage
 * call is wrapped, so a blocked, full or corrupted store never breaks the editor.
 */

export const CABIN_PRESET_STORAGE_KEY = 'flightline.cabinPresets.v1';
export const MAX_PRESET_NAME_LENGTH = 40;

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function parseCabin(value: unknown): CabinConfiguration | null {
  if (!isRecord(value)) {
    return null;
  }
  const {
    economySeats,
    economyPitchInches,
    premiumEconomySeats,
    businessSeats,
    businessPitchInches,
    firstSeats,
    galleyCount,
    wifiEquipped,
    inSeatPower,
  } = value;
  if (
    isFiniteNumber(economySeats) &&
    isFiniteNumber(economyPitchInches) &&
    isFiniteNumber(premiumEconomySeats) &&
    isFiniteNumber(businessSeats) &&
    isFiniteNumber(businessPitchInches) &&
    isFiniteNumber(firstSeats) &&
    isFiniteNumber(galleyCount) &&
    typeof wifiEquipped === 'boolean' &&
    typeof inSeatPower === 'boolean'
  ) {
    return {
      economySeats,
      economyPitchInches,
      premiumEconomySeats,
      businessSeats,
      businessPitchInches,
      firstSeats,
      galleyCount,
      wifiEquipped,
      inSeatPower,
    };
  }
  return null;
}

function parsePreset(value: unknown): SavedCabinPreset | null {
  if (!isRecord(value)) {
    return null;
  }
  const { id, name, typeId, cabin, savedAtIso } = value;
  const parsedCabin = parseCabin(cabin);
  if (
    typeof id === 'string' &&
    id.length > 0 &&
    typeof name === 'string' &&
    name.length > 0 &&
    typeof typeId === 'string' &&
    typeof savedAtIso === 'string' &&
    parsedCabin !== null
  ) {
    return { id, name, typeId, cabin: parsedCabin, savedAtIso };
  }
  return null;
}

function loadPresets(): readonly SavedCabinPreset[] {
  try {
    const raw = window.localStorage.getItem(CABIN_PRESET_STORAGE_KEY);
    if (raw === null) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    const presets: SavedCabinPreset[] = [];
    for (const entry of parsed as readonly unknown[]) {
      const preset = parsePreset(entry);
      if (preset !== null) {
        presets.push(preset);
      }
    }
    return presets;
  } catch {
    return [];
  }
}

function persistPresets(presets: readonly SavedCabinPreset[]): void {
  try {
    window.localStorage.setItem(CABIN_PRESET_STORAGE_KEY, JSON.stringify(presets));
  } catch {
    // Storage unavailable or full: presets stay in memory for this session.
  }
}

function createPresetId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `preset-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface CabinPresetStore {
  readonly presets: readonly SavedCabinPreset[];
  /** Saves a preset for an aircraft type and returns it. A blank name is rejected (null). */
  readonly savePreset: (
    name: string,
    typeId: AircraftTypeId,
    cabin: CabinConfiguration
  ) => SavedCabinPreset | null;
  readonly deletePreset: (id: string) => void;
}

export function useCabinPresets(): CabinPresetStore {
  const [presets, setPresets] = useState<readonly SavedCabinPreset[]>(loadPresets);

  const savePreset = useCallback(
    (name: string, typeId: AircraftTypeId, cabin: CabinConfiguration): SavedCabinPreset | null => {
      const trimmed = name.trim().slice(0, MAX_PRESET_NAME_LENGTH);
      if (trimmed.length === 0) {
        return null;
      }
      const preset: SavedCabinPreset = {
        id: createPresetId(),
        name: trimmed,
        typeId,
        cabin,
        savedAtIso: new Date().toISOString(),
      };
      setPresets((current) => {
        const next = [...current, preset];
        persistPresets(next);
        return next;
      });
      return preset;
    },
    []
  );

  const deletePreset = useCallback((id: string): void => {
    setPresets((current) => {
      const next = current.filter((preset) => preset.id !== id);
      persistPresets(next);
      return next;
    });
  }, []);

  return { presets, savePreset, deletePreset };
}
